import { useState, useEffect, useMemo, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { opportunitiesApi, usersApi, configApi, adminApi, dashboardApi, timelineApi, api } from '../../services/api'
import { Card, Input, Select, Flex, Badge, Spinner } from '../../components/shared/StyledComponents'
import { OpportunityModal } from './components/OpportunityModal'
import { RedmineCardPicker } from './components/RedmineCardPicker'
import { useAuth } from '../../contexts/AuthContext'
import { INCOMPLETE_STATE, missingFieldsText } from '../../utils/offerCompleteness'

// Format status labels to be human-readable
const formatStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
        ganada: 'Ganada',
        perdida: 'Perdida',
        cancelada: 'Cancelada',
        en_curso: 'En Curso',
        en_negociacion: 'En Negociación',
        presentada: 'Presentada',
        en_preparacion: 'En Preparación',
        en_revision: 'En Revisión',
        borrador: 'Borrador',
        activo: 'Activo',
        pendiente: 'Pendiente',
        prospecto: 'Prospecto',
        planificacion: 'Planificación',
        finalizado: 'Finalizado',
        completado: 'Completado',
        cerrado: 'Cerrado',
        no_iniciado: 'No Iniciado',
        en_espera: 'En Espera',
        aprobada: 'Aprobada',
        rechazada: 'Rechazada',
        incompleta: 'Incompleta',
    }
    return statusMap[status?.toLowerCase()] || status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Sin estado'
}

// oferta.probabilidad is a level ('alta' | 'media' | 'baja'), not a number:
// printing it with a '%' gave "alta%". Legacy numeric values keep the '%'.
const PROBABILITY_LABELS: Record<string, string> = { alta: 'Alta', media: 'Media', baja: 'Baja' }

export const formatProbability = (value: unknown): string | null => {
    if (value === null || value === undefined || value === '') return null
    if (typeof value === 'number' || /^\d+(\.\d+)?$/.test(String(value).trim())) return `${value}%`
    const key = String(value).trim().toLowerCase()
    return PROBABILITY_LABELS[key] ?? String(value)
}

// Team members come from the API as {user_id, username, display_name,
// capacity_pct, ...}; older saved rows use {userId, userName, load}. Read
// both so the card never renders blank names ("👥 , ").
export const normalizeTeam = (members: any[] | null | undefined): { userName: string; load: number }[] =>
    (members || [])
        .map((m: any) => ({
            userName: m?.userName || m?.user_name || m?.display_name || m?.username || '',
            load: m?.load ?? m?.capacity_pct ?? 0,
        }))
        .filter((m) => m.userName)

const PageContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  max-width: 1400px;
  margin: 0 auto;
`

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`

const PageTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
`

const CreateButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
  }
`

const FiltersCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 500;
`

const OpportunitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`

const OpportunityCard = styled(Card)`
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  border-left: 4px solid transparent;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`

interface OpportunityCardStyledProps {
    status: string
}

const OpportunityCardStyled = styled(OpportunityCard) <OpportunityCardStyledProps>`
  border-left-color: ${({ status }) => {
        switch (status) {
            case 'ganada':
                return '#2E7D32'
            case 'perdida':
            case 'cancelada':
                return '#C62828'
            case 'en_curso':
            case 'en_negociacion':
                return '#FF9800'
            case 'presentada':
                return '#7B1FA2'
            case 'en_preparacion':
            case 'en_revision':
                return '#1565C0'
            case 'incompleta':
                return '#FF5722'
            case 'borrador':
            default:
                return '#9E9E9E'
        }
    }};
`

const OpportunityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

const OpportunityTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.text};
`

const OpportunityClient = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const OpportunityMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};
`

const MetaItem = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: 4px;
`

const StatusBadge = styled(Badge) <{ status: string }>`
  background: ${({ status }) => {
        switch (status) {
            case 'ganada':
                return '#2E7D32'
            case 'perdida':
            case 'cancelada':
                return '#C62828'
            case 'en_curso':
            case 'en_negociacion':
                return '#FF9800'
            case 'presentada':
                return '#7B1FA2'
            case 'en_preparacion':
            case 'en_revision':
                return '#1565C0'
            case 'incompleta':
                return '#FF5722'
            case 'borrador':
            default:
                return '#9E9E9E'
        }
    }};
  color: white;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`

const NoResults = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textMuted};
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl};
`

const ResultsCount = styled.p`
  color: ${({ theme }) => theme.colors.textMuted};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
`

interface Opportunity {
    id: string
    name: string
    client: string
    gestor?: string
    status: string
    stage: string
    value?: number
    probability?: string | number
    startDate?: string
    endDate?: string
    owner?: string
    technologies?: string[]
    sector?: string
    team?: { userName: string; load: number }[]
    tipoProyecto?: string
    proyectoInfra?: string
    infraAssignments?: Array<{ label: string }>
    rolesRequeridos?: Array<{ role: string; count: number; horas_totales?: number }>
    pendingReview?: boolean
    /** What an offer the import marked 'incompleta' still lacks (backend keys). */
    missingFields?: string[]
    redmineIdentifier?: string | null
    redmineProjectId?: number | null
    redmineProjectName?: string | null
}

// Under the client line of an 'Incompleta' card: what the import could not fill.
const MissingNote = styled.div`
  font-size: 0.75rem;
  color: #BF360C;
  margin: -4px 0 6px 0;
`

const SharePointBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #FFF3E0;
  color: #E65100;
  border: 1px solid #FFB74D;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
`

type BillingTone = 'ok' | 'warning' | 'alert' | 'unknown'

const BILLING_TONES: Record<BillingTone, { bg: string; fg: string; border: string }> = {
    ok: { bg: '#E8F5E9', fg: '#2E7D32', border: '#A5D6A7' },
    warning: { bg: '#FFF3E0', fg: '#E65100', border: '#FFB74D' },
    alert: { bg: '#FFEBEE', fg: '#C62828', border: '#EF9A9A' },
    unknown: { bg: '#F5F5F5', fg: '#757575', border: '#E0E0E0' },
}

const BillingBadge = styled.span<{ $tone: BillingTone }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 8px;
  background: ${({ $tone }) => BILLING_TONES[$tone].bg};
  color: ${({ $tone }) => BILLING_TONES[$tone].fg};
  border: 1px solid ${({ $tone }) => BILLING_TONES[$tone].border};
`

/** One entry of GET /billing/opportunity-status (see billing.py for the full shape). */
interface OpportunityBillingStatus {
    redmine_code?: string
    total_budget?: number | null
    hours_used?: number | null
    pct_consumed?: number | null
    alert?: {
        type: 'extension_needed' | 'budget_warning'
        extension_hours?: number | null
        redmine_code?: string
        message?: string
    } | null
    redmine_error?: string | null
    /** 'time_material': billed by the hours logged in Redmine (no bag of hours). */
    billing_mode?: string | null
    hours_logged_month?: number | null
}

const formatHours = (h: number): string => (Math.round(h * 10) / 10).toLocaleString('es-ES')

/**
 * The card's month-end forecast badge. The alert may come from a subproject
 * (its message is prefixed with the subproject code), so it is shown even when
 * the parent has no % figure. A null % with redmine_error means Redmine could
 * not be read, which is "unknown", not 0 %.
 */
export const OpportunityBillingBadge: React.FC<{ status?: OpportunityBillingStatus | null }> = ({ status }) => {
    if (!status) return null
    const pct = status.pct_consumed
    const alert = status.alert || null
    const hasPct = pct !== null && pct !== undefined
    const isTm = status.billing_mode === 'time_material'
    const tmHours = status.hours_logged_month
    if (!hasPct && !alert && !status.redmine_error && !isTm) return null

    const parts: string[] = []
    if (hasPct) parts.push(`⏱️ ${formatHours(pct)}%`)
    else if (isTm && tmHours !== null && tmHours !== undefined) parts.push(`🧾 T&M · ${formatHours(tmHours)} h este mes`)
    else if (!alert) parts.push('⏱️ N/D')
    if (alert?.type === 'extension_needed') {
        parts.push(alert.extension_hours ? `🚨 Pedir ampliación +${formatHours(alert.extension_hours)}h` : '🚨 Pedir ampliación')
    } else if (alert?.type === 'budget_warning') {
        // A subproject warning can arrive without a parent %; don't leave a bare icon
        parts.push(hasPct ? '⚠️' : '⚠️ Aviso de horas')
    }

    const tone: BillingTone = alert?.type === 'extension_needed' ? 'alert'
        : alert?.type === 'budget_warning' ? 'warning'
        : hasPct || (isTm && tmHours != null) ? 'ok' : 'unknown'
    const title = [
        alert?.message || (hasPct ? `${formatHours(pct)}% de las horas habilitadas consumido` : null),
        isTm ? 'Time & Material: se factura por las horas imputadas en Redmine' : null,
        status.redmine_error ? `Redmine no disponible: ${status.redmine_error}` : null,
    ].filter(Boolean).join(' · ')

    return (
        <BillingBadge $tone={tone} title={title} data-testid="billing-badge">
            {parts.join(' ')}
        </BillingBadge>
    )
}

export const OportunidadesPage: React.FC = () => {
    const location = useLocation()
    const navigate = useNavigate()
    // The page is also rendered without an AuthProvider in unit tests; treat that as "no user".
    let authRole: string | undefined
    try {
        authRole = useAuth().user?.role
    } catch {
        authRole = undefined
    }
    const canLinkRedmine = authRole?.toLowerCase() === 'admin'
    const [opportunities, setOpportunities] = useState<Opportunity[]>([])
    const [rawOpportunities, setRawOpportunities] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [stageFilter, setStageFilter] = useState('')
    const [ownerFilter, setOwnerFilter] = useState('')
    const [sectorFilter, setSectorFilter] = useState('')
    const [techFilter, setTechFilter] = useState('')
    const [tipoProyectoFilter, setTipoProyectoFilter] = useState('')
    const [showModal, setShowModal] = useState(false)
    const [editingOpportunity, setEditingOpportunity] = useState<any | null>(null)
    // Bumped every time the modal is opened, so a late async step (the DELETE of
    // a discarded draft) can tell whether the modal still shows what it acted on.
    const modalSessionRef = useRef(0)
    const openModal = (rawOpp: any | null) => {
        modalSessionRef.current += 1
        setEditingOpportunity(rawOpp)
        setShowModal(true)
    }
    // Only the newest list request may write the state: closing the modal and a
    // draft's DELETE both reload, and an older response landing last would bring
    // the discarded draft (or stale fields) back.
    const loadSeqRef = useRef(0)
    const billingSeqRef = useRef(0)
    const [sectors, setSectors] = useState<string[]>([])
    const [technologies, setTechnologies] = useState<Record<string, string[]>>({})
    const [users, setUsers] = useState<{ id: string; name: string; role?: string }[]>([])
    const [availableRoles, setAvailableRoles] = useState<string[]>([])
    const [autocompleteData, setAutocompleteData] = useState<{ gestores: string[]; responsables: string[] }>({ gestores: [], responsables: [] })
    const [infraMap, setInfraMap] = useState<Record<string, string>>({})
    const [syncing, setSyncing] = useState(false)
    const [syncProgress, setSyncProgress] = useState<string>('')
    const [syncPhase, setSyncPhase] = useState<string>('discovery')
    const [syncPct, setSyncPct] = useState(0)
    // Track the job_id so the Cancel button can target the right sync. Set
    // when the initiator starts a sync or when an observer attaches to a
    // running job via /sync-sharepoint/current.
    const [syncJobId, setSyncJobId] = useState<string | null>(null)
    const [cancelling, setCancelling] = useState(false)
    const [previewData, setPreviewData] = useState<any[] | null>(null)
    // Paths ticked in the preview modal (key: "{sector}/{client}/{opp_name}").
    // Seeded to every non-excluded item when the preview arrives so the default
    // is "import everything that fits"; users can deselect before confirming.
    const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set())
    const [previewLoading, setPreviewLoading] = useState(false)
    // Which rows the preview shows: every row, or only one status.
    const [previewFilter, setPreviewFilter] = useState<'all' | 'ready' | 'exists' | 'excluded'>('all')
    const [billingStatuses, setBillingStatuses] = useState<Record<string, any>>({})

    // Load everything once per mount (each visit to the page mounts it again).
    // A second "refreshKey" bump on mount used to run every loader twice,
    // which now matters: the billing status runs the Redmine forecast.
    useEffect(() => {
        loadOpportunities()
        loadConfig()
        loadUsers()
        loadAutocomplete()
        loadInfraOptions()
        loadBillingStatuses()
    }, [])

    // Month-end forecast per opportunity for the card badge. Shape documented
    // in billing.py get_opportunity_billing_status: {statuses: {opp_id: {
    // pct_consumed|null, alert: null|{type, extension_hours, redmine_code,
    // message}, redmine_error|null, ...}}}. The page is admin-only, as is the
    // data; a failure just leaves the badges out.
    const loadBillingStatuses = async () => {
        // Every modal close reloads this too; keep only the newest answer.
        const seq = ++billingSeqRef.current
        try {
            const res = await api.billing.getOpportunityStatus()
            if (seq === billingSeqRef.current) setBillingStatuses(res.data?.statuses || {})
        } catch {
            if (seq === billingSeqRef.current) setBillingStatuses({})
        }
    }

    // Auto-open the edit modal when another page navigated here with
    // state.openOpportunityId (the contract in utils/openOpportunity).
    useEffect(() => {
        const state = location.state as { openOpportunityId?: number | string } | null
        if (state?.openOpportunityId && rawOpportunities.length > 0) {
            const rawOpp = rawOpportunities.find(
                (o: any) => String(o.id) === String(state.openOpportunityId)
            )
            if (rawOpp) {
                openModal(rawOpp)
            }
            // Clear the state through the router, not window.history: the
            // router's location kept the old state, so every later reload of
            // the list (save, Redmine link) re-opened the modal.
            navigate(location.pathname, { replace: true, state: null })
        }
    }, [location.state, rawOpportunities])

    // value → label for the infra options; the card uses it for the virtual
    // 'local' / 'cliente' values when there is no assignment row.
    const loadInfraOptions = async () => {
        try {
            const res = await api.infrastructure.getProjectOptions()
            const map: Record<string, string> = {}
            ;(Array.isArray(res.data) ? res.data : []).forEach((o: any) => {
                if (o?.value) map[o.value] = String(o.label ?? o.value).trim()
            })
            setInfraMap(map)
        } catch (error) {
            console.error('Error loading infra options:', error)
        }
    }

    const loadAutocomplete = async () => {
        try {
            const res = await opportunitiesApi.getAutocomplete()
            setAutocompleteData(res.data || { gestores: [], responsables: [] })
        } catch (error) {
            console.error('Error loading autocomplete data:', error)
        }
    }

    const loadUsers = async () => {
        try {
            const res = await usersApi.getAll()
            const data = Array.isArray(res.data) ? res.data : []
            const mapped = data
                .filter((u: any) => {
                    // Admin user has username 'admin' - exclude from user selection
                    const username = (u.username || '').toLowerCase()
                    return username !== 'admin'
                })
                .map((u: any) => ({
                    id: String(u.id),
                    name: u.display_name || u.username || 'Sin nombre',
                    role: u.job_role || u.department || '',
                }))
            setUsers(mapped)
        } catch (error) {
            console.error('Error loading users:', error)
        }
    }

    const loadConfig = async () => {
        try {
            const [categoriesRes, rolesRes] = await Promise.all([
                configApi.getCategories(),
                configApi.getRoles(),
            ])
            setSectors(categoriesRes.data?.sectores || [])
            setTechnologies(categoriesRes.data?.tecnologias || {})

            // Combine seniority levels with roles (skip level prefix for leadership/special roles)
            const roles = rolesRes.data?.roles || []
            const noLevelPrefix: string[] = rolesRes.data?.no_level_prefix || []
            const experienceLevels = rolesRes.data?.experience_levels || {}
            const seniorityLabels = Object.keys(experienceLevels) // ['Junior', 'Mid-Senior', 'Senior']
            const combinedRoles: string[] = []
            roles.forEach((role: string) => {
                if (noLevelPrefix.includes(role)) {
                    // Leadership/special roles: no experience prefix
                    combinedRoles.push(role)
                } else {
                    // Technical roles: combine with seniority levels
                    seniorityLabels.forEach(seniority => {
                        combinedRoles.push(`${seniority} ${role}`)
                    })
                }
            })
            setAvailableRoles(combinedRoles)
        } catch (error) {
            console.error('Error loading config:', error)
        }
    }

    // background: refresh without swapping the grid for the spinner (used when
    // the modal closes, which happens on every edit session).
    const loadOpportunities = async ({ background = false }: { background?: boolean } = {}) => {
        const seq = ++loadSeqRef.current
        const isLatest = () => seq === loadSeqRef.current
        try {
            if (!background) setLoading(true)
            const response = await opportunitiesApi.getAll()
            if (!isLatest()) return
            // API returns array directly or {data: [...]} - handle both
            const data = Array.isArray(response.data) ? response.data : (response.data?.opportunities || [])
            // Assigned GPU / partition per opportunity (the source of truth for
            // the 🖥️ chips), in one request for the whole list. Non-critical:
            // on failure the cards fall back to proyecto_infra.
            let assignmentsMap: Record<string, Array<{ label: string }>> = {}
            const ids = data.map((o: any) => Number(o.id)).filter((id: number) => Number.isInteger(id) && id > 0)
            if (ids.length) {
                try {
                    const res = await api.infrastructure.getAssignmentsByOpportunities(ids)
                    assignmentsMap = res.data || {}
                } catch (error) {
                    console.error('Error loading infra assignments:', error)
                }
            }
            if (!isLatest()) return
            // Map backend structure to frontend structure
            const mapped = data.map((opp: any) => ({
                id: opp.id,
                name: opp.general?.name || opp.name || 'Sin nombre',
                client: opp.general?.cliente || opp.client || '-',
                gestor: opp.general?.gestor || '-',
                status: opp.oferta?.estado || opp.proyecto?.estado || opp.status || 'Sin estado',
                stage: opp.proyecto?.estado || opp.stage || '-',
                value: opp.general?.proyecto_interno ? undefined : (opp.oferta?.venta || opp.oferta?.amount || opp.value),
                probability: opp.oferta?.probabilidad ?? opp.probability ?? undefined,
                startDate: opp.oferta?.fecha_inicio || opp.startDate,
                endDate: opp.proyecto?.fecha_fin || opp.endDate,
                owner: opp.oferta?.responsable || opp.owner,
                technologies: (opp.general?.tecnologias?.length ? opp.general.tecnologias : opp.general?.tecnologia ? [opp.general.tecnologia] : opp.technologies) || [],
                sector: opp.general?.sector || opp.sector || '',
                tipoProyecto: opp.general?.proyecto_interno ? 'Proyecto interno' : (opp.general?.tipo_proyecto || ''),
                // [] is truthy, so `a || b` never fell back to the oferta team
                team: normalizeTeam(opp.proyecto?.equipo?.length ? opp.proyecto.equipo : opp.oferta?.equipo),
                proyectoInfra: opp.proyecto_infra || undefined,
                infraAssignments: assignmentsMap[String(opp.id)],
                rolesRequeridos: opp.oferta?.roles_requeridos || [],
                pendingReview: opp.pending_review || false,
                missingFields: opp.oferta?.missing_fields || [],
                redmineIdentifier: opp.proyecto?.redmine_codigo_imputacion || opp.proyecto?.redmineCodigoImputacion || null,
                redmineProjectId: opp.proyecto?.redmine_project_id ?? null,
                redmineProjectName: opp.proyecto?.redmine_project_name || null,
            }))
            // raw and mapped are written together, after the last await, so the
            // modal is never seeded from a list newer or older than the cards.
            setRawOpportunities(data)
            setOpportunities(mapped)
        } catch (error) {
            console.error('Error loading opportunities:', error)
            // A failed background refresh keeps the cards on screen.
            if (isLatest() && !background) setOpportunities([])
        } finally {
            if (isLatest()) setLoading(false)
        }
    }

    const statuses = useMemo(() => {
        const unique = [...new Set(opportunities.map((o) => o.status))]
        return unique.filter(Boolean).sort()
    }, [opportunities])

    const stages = useMemo(() => {
        const unique = [...new Set(opportunities.map((o) => o.stage))]
        return unique.filter(Boolean).sort()
    }, [opportunities])

    const owners = useMemo(() => {
        const unique = [...new Set(opportunities.map((o) => o.owner))]
        return unique.filter(Boolean).sort()
    }, [opportunities])

    const filteredOpportunities = useMemo(() => {
        return opportunities.filter((opp) => {
            const matchesSearch =
                !searchTerm ||
                opp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                opp.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (opp.gestor || '').toLowerCase().includes(searchTerm.toLowerCase())

            const matchesStatus = !statusFilter || opp.status === statusFilter
            const matchesStage = !stageFilter || opp.stage === stageFilter
            const matchesOwner = !ownerFilter || opp.owner === ownerFilter
            const matchesSector = !sectorFilter || opp.sector === sectorFilter
            const matchesTech = !techFilter || opp.technologies?.includes(techFilter)
            const matchesTipo = !tipoProyectoFilter || opp.tipoProyecto === tipoProyectoFilter

            return matchesSearch && matchesStatus && matchesStage && matchesOwner && matchesSector && matchesTech && matchesTipo
        })
    }, [opportunities, searchTerm, statusFilter, stageFilter, ownerFilter, sectorFilter, techFilter, tipoProyectoFilter])

    const formatCurrency = (value?: number) => {
        if (!value) return '-'
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: 0,
        }).format(value)
    }

    const handleOpportunityClick = (opportunity: Opportunity) => {
        // Find raw opportunity data for editing
        const rawOpp = rawOpportunities.find((o: any) => String(o.id) === String(opportunity.id))
        openModal(rawOpp || null)
    }

    const handleCreateNew = () => {
        openModal(null)
    }

    const pollSyncJob = async (jobId: string, showAlertOnComplete: boolean): Promise<void> => {
        let job: any
        try {
            const statusRes = await api.opportunities.syncSharepointStatus(jobId)
            job = statusRes.data
        } catch (err: any) {
            // If the job row no longer exists (e.g. it was purged or the job_id
            // is stale after a redeploy), clear local state and stop polling.
            if (err?.response?.status === 404) {
                setSyncing(false)
                setSyncProgress('')
                return
            }
            // Any other transient error (500, network blip): wait and retry
            // instead of tearing down the progress bar.
            await new Promise((r) => setTimeout(r, 3000))
            return pollSyncJob(jobId, showAlertOnComplete)
        }

        const processed = job.processed ?? 0
        const total = job.total ?? 0
        const phase = job.phase || 'discovery'
        const startedBy = job.started_by ? ` (${job.started_by})` : ''
        const current = job.current_folder ? ` — ${job.current_folder}` : ''

        setSyncPhase(phase)
        setSyncPct(total > 0 ? Math.round((processed / total) * 100) : 0)

        if (phase === 'discovery') {
            setSyncProgress(`Descubriendo carpetas en SharePoint...${startedBy}`)
        } else {
            setSyncProgress(
                `${processed}/${total} procesadas | ${job.created ?? 0} creadas, ${job.skipped ?? 0} omitidas, ${job.errors ?? 0} errores${current}${startedBy}`
            )
        }

        if (job.status === 'running') {
            await new Promise((r) => setTimeout(r, 3000))
            return pollSyncJob(jobId, showAlertOnComplete)
        }

        if (job.status === 'failed') {
            throw new Error(job.error || 'Sincronización fallida')
        }

        // Cancelled — treat like a clean finish (no error), clear the bar.
        // Only the initiator/cancelling admin sees the toast; observers just
        // see the progress card disappear.
        if (job.status === 'cancelled') {
            if (showAlertOnComplete) {
                alert(job.error || 'Sincronización cancelada')
            }
            loadOpportunities()
            return
        }

        // Completed — only the initiator gets the toast; observers (Admin B
        // attaching via /sync-sharepoint/current) just get the bar cleared.
        if (showAlertOnComplete) {
            alert('Sincronización completada')
        }
        loadOpportunities()
    }

    const handleCancelSync = async () => {
        if (!syncJobId || cancelling) return
        if (!window.confirm('¿Cancelar la sincronización en curso?')) return
        setCancelling(true)
        try {
            await api.opportunities.syncSharepointCancel(syncJobId)
            // The poll loop will notice `status='cancelled'` on its next tick
            // and tear the bar down. Flip local state right away so the UI
            // doesn't flicker another spinner frame.
            setSyncing(false)
            setSyncProgress('')
            setSyncJobId(null)
        } catch (err: any) {
            const detail =
                err?.response?.data?.detail || err?.message || 'Error al cancelar la sincronización'
            alert(`Error: ${detail}`)
        } finally {
            setCancelling(false)
        }
    }

    const handleSyncSharepoint = async () => {
        if (syncing) return
        // First: show preview modal
        setPreviewLoading(true)
        setPreviewData(null)
        setPreviewFilter('all')
        setSelectedPaths(new Set())
        try {
            const res = await api.opportunities.syncSharepointPreview()
            const items: any[] = res.data || []
            setPreviewData(items)
            // Default selection: every importable item. Excluded items have no
            // checkbox (they can't be processed anyway).
            setSelectedPaths(
                new Set(
                    items
                        .filter((p) => p.status !== 'excluded' && typeof p.path === 'string')
                        .map((p) => p.path as string)
                )
            )
        } catch (error: any) {
            const detail = error?.response?.data?.detail || error?.message || 'Error al escanear SharePoint'
            alert(`Error: ${detail}`)
        } finally {
            setPreviewLoading(false)
        }
    }

    const toggleSelectedPath = (path: string) => {
        setSelectedPaths((prev) => {
            const next = new Set(prev)
            if (next.has(path)) next.delete(path)
            else next.add(path)
            return next
        })
    }

    const handleConfirmSync = async () => {
        // Snapshot the selection before we clear the modal.
        const paths = Array.from(selectedPaths)
        setPreviewData(null)
        setSyncing(true)
        setSyncProgress('Iniciando sincronización...')
        try {
            // Back-compat: if paths is empty, don't send `paths` at all and
            // let the backend process everything (legacy behaviour / observer
            // triggering re-sync).
            const res = paths.length > 0
                ? await opportunitiesApi.syncSharepoint({ paths })
                : await opportunitiesApi.syncSharepoint()
            const data = res.data
            const jobId = data?.job_id
            if (!jobId) {
                throw new Error('No se recibió job_id del backend')
            }
            if (data?.already_running) {
                setSyncProgress(data.message || 'Sincronización ya en curso')
            }
            setSyncJobId(jobId)
            await pollSyncJob(jobId, true)
        } catch (error: any) {
            const detail = error?.response?.data?.detail || error?.message || 'Error al sincronizar'
            alert(`Error: ${detail}`)
        } finally {
            setSyncing(false)
            setSyncProgress('')
            setSyncJobId(null)
        }
    }

    // On mount: if another admin is running a sync, attach to it so we see the
    // same progress bar they do. Without this, Admin B opening this page while
    // Admin A is syncing would see a blank header and have no idea a sync is
    // in flight. The `/sync-sharepoint/current` endpoint returns the active job
    // or null; on match we flip into `syncing=true` and start polling.
    useEffect(() => {
        let cancelled = false
        api.opportunities
            .syncSharepointCurrent()
            .then((res) => {
                if (cancelled) return
                const job = res?.data
                if (job && job.status === 'running' && job.job_id) {
                    setSyncing(true)
                    setSyncJobId(job.job_id)
                    setSyncProgress(
                        `Sincronizando… (iniciado por ${job.started_by ?? 'otro admin'})`
                    )
                    // Observer — don't toast on completion (we didn't start it)
                    pollSyncJob(job.job_id, false)
                        .catch(() => {
                            setSyncing(false)
                            setSyncProgress('')
                        })
                        .finally(() => {
                            setSyncJobId(null)
                        })
                }
            })
            .catch(() => {
                // No active sync, 401, or network hiccup — stay silent.
            })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSaveOpportunity = async (data: any) => {
        try {
            const { offerFile, rfpFiles, ...opportunityData } = data
            let opportunityId: number

            if (data.id) {
                await opportunitiesApi.update(parseInt(data.id), opportunityData)
                opportunityId = parseInt(data.id)
            } else {
                const response = await opportunitiesApi.create(opportunityData)
                opportunityId = response.data.id
            }

            // Upload files if provided
            if (offerFile && opportunityId) {
                try {
                    await opportunitiesApi.uploadFile(opportunityId, offerFile, 'oferta')
                } catch (fileError) {
                    console.error('Error uploading offer file:', fileError)
                    alert('Oportunidad guardada, pero hubo un error al subir el documento de oferta.')
                }
            }
            if (rfpFiles && rfpFiles.length > 0 && opportunityId) {
                for (const rfpFile of rfpFiles) {
                    try {
                        await opportunitiesApi.uploadFile(opportunityId, rfpFile, 'rfp')
                    } catch (fileError) {
                        console.error('Error uploading RFP file:', fileError)
                        alert(`Oportunidad guardada, pero hubo un error al subir el pliego/RFP: ${rfpFile.name}`)
                    }
                }
            }

            loadOpportunities()
            loadAutocomplete()
            return opportunityId
        } catch (error: any) {
            console.error('Error saving opportunity:', error)
            if (error.response?.status === 409) {
                alert(error.response.data?.detail || 'Ya existe una oportunidad con ese nombre, código de oferta o código de proyecto')
                return undefined
            }
            throw error
        }
    }

    const handleDeleteOpportunity = async (id: string) => {
        // The modal also calls this to discard an untouched draft, right before
        // it closes. If the user opens another card while the DELETE is in
        // flight, closing here would shut that new modal: only close when the
        // modal is still the session that asked for the delete.
        const session = modalSessionRef.current
        try {
            await opportunitiesApi.delete(parseInt(id))
            if (modalSessionRef.current === session) {
                setShowModal(false)
                setEditingOpportunity(null)
            }
            loadOpportunities({ background: true })
        } catch (error: any) {
            console.error('Error deleting opportunity:', error)
            alert(error.response?.data?.detail || 'Error al eliminar la oportunidad')
        }
    }

    // Technology categories (high level)
    const techCategories = Object.keys(technologies)

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>🎯 Oportunidades</PageTitle>
                <Flex style={{ gap: '8px' }}>
                    <CreateButton onClick={handleSyncSharepoint} disabled={syncing || previewLoading} style={{ background: syncing || previewLoading ? '#9E9E9E' : '#1565C0' }}>
                        {syncing ? '⏳ Sincronizando...' : previewLoading ? '🔍 Escaneando...' : '📂 Importar SharePoint'}
                    </CreateButton>
                    <CreateButton onClick={handleCreateNew}>
                        ➕ Nueva Oportunidad
                    </CreateButton>
                </Flex>
            </PageHeader>

            {syncing && syncProgress && (
                <Card style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: '#E3F2FD', borderLeft: '4px solid #1565C0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: syncPhase === 'processing' ? '0.5rem' : 0 }}>
                        <div style={{ flex: 1, fontSize: '0.9rem', color: '#1565C0', fontWeight: 500 }}>
                            {syncPhase === 'discovery' ? '🔍' : '📂'} {syncProgress}
                        </div>
                        {syncJobId && (
                            <button
                                onClick={handleCancelSync}
                                disabled={cancelling}
                                title="Cancelar sincronización en curso"
                                style={{
                                    padding: '0.35rem 0.75rem',
                                    fontSize: '0.8rem',
                                    background: cancelling ? '#9E9E9E' : '#C62828',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: cancelling ? 'not-allowed' : 'pointer',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 500,
                                }}
                            >
                                {cancelling ? 'Cancelando…' : '✕ Cancelar'}
                            </button>
                        )}
                    </div>
                    {syncPhase === 'processing' && (
                        <div style={{ background: '#BBDEFB', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                            <div style={{
                                width: `${syncPct}%`,
                                height: '100%',
                                background: '#1565C0',
                                borderRadius: '4px',
                                transition: 'width 0.5s ease',
                            }} />
                        </div>
                    )}
                </Card>
            )}

            <FiltersCard>
                <FiltersGrid>
                    <FilterGroup>
                        <FilterLabel>Buscar</FilterLabel>
                        <Input
                            type="text"
                            placeholder="Nombre o cliente..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </FilterGroup>
                    <FilterGroup>
                        <FilterLabel>Estado</FilterLabel>
                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="">Todos</option>
                            {statuses.map((status) => (
                                <option key={status} value={status}>
                                    {formatStatusLabel(status)}
                                </option>
                            ))}
                        </Select>
                    </FilterGroup>
                    <FilterGroup>
                        <FilterLabel>Fase</FilterLabel>
                        <Select
                            value={stageFilter}
                            onChange={(e) => setStageFilter(e.target.value)}
                        >
                            <option value="">Todas</option>
                            {stages.map((stage) => (
                                <option key={stage} value={stage}>
                                    {formatStatusLabel(stage)}
                                </option>
                            ))}
                        </Select>
                    </FilterGroup>
                    <FilterGroup>
                        <FilterLabel>Comercial</FilterLabel>
                        <Select
                            value={ownerFilter}
                            onChange={(e) => setOwnerFilter(e.target.value)}
                        >
                            <option value="">Todos</option>
                            {owners.map((owner) => (
                                <option key={owner} value={owner}>
                                    {owner}
                                </option>
                            ))}
                        </Select>
                    </FilterGroup>
                    <FilterGroup>
                        <FilterLabel>Sector</FilterLabel>
                        <Select
                            value={sectorFilter}
                            onChange={(e) => setSectorFilter(e.target.value)}
                        >
                            <option value="">Todos</option>
                            {sectors.map((sector) => (
                                <option key={sector} value={sector}>
                                    {sector}
                                </option>
                            ))}
                        </Select>
                    </FilterGroup>
                    <FilterGroup>
                        <FilterLabel>Tecnología</FilterLabel>
                        <Select
                            value={techFilter}
                            onChange={(e) => setTechFilter(e.target.value)}
                        >
                            <option value="">Todas</option>
                            {techCategories.map((tech) => (
                                <option key={tech} value={tech}>
                                    {tech}
                                </option>
                            ))}
                        </Select>
                    </FilterGroup>
                    <FilterGroup>
                        <FilterLabel>Tipo de Proyecto</FilterLabel>
                        <Select
                            value={tipoProyectoFilter}
                            onChange={(e) => setTipoProyectoFilter(e.target.value)}
                        >
                            <option value="">Todos</option>
                            <option value="Proyecto interno">Proyecto interno</option>
                            <option value="Producto">Producto</option>
                            <option value="Servicio">Servicio</option>
                            <option value="Consultoria">Consultoría</option>
                            <option value="I+D+i">I+D+i</option>
                            <option value="PoC">PoC</option>
                            <option value="Otros">Otros</option>
                        </Select>
                    </FilterGroup>
                </FiltersGrid>
            </FiltersCard>

            {loading ? (
                <LoadingContainer>
                    <Spinner />
                </LoadingContainer>
            ) : (
                <>
                    <ResultsCount>
                        {filteredOpportunities.length} oportunidad
                        {filteredOpportunities.length !== 1 ? 'es' : ''} encontrada
                        {filteredOpportunities.length !== 1 ? 's' : ''}
                    </ResultsCount>

                    {filteredOpportunities.length === 0 ? (
                        <NoResults>
                            <p>No se encontraron oportunidades con los filtros aplicados</p>
                        </NoResults>
                    ) : (
                        <OpportunitiesGrid>
                            {filteredOpportunities.map((opp) => (
                                <OpportunityCardStyled
                                    key={opp.id}
                                    status={opp.status}
                                    onClick={() => handleOpportunityClick(opp)}
                                >
                                    <OpportunityHeader>
                                        <OpportunityTitle>{opp.name}</OpportunityTitle>
                                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                            {opp.pendingReview && (
                                                <SharePointBadge title="Importada desde SharePoint - Pendiente de revisión">
                                                    📂 Revisar
                                                </SharePointBadge>
                                            )}
                                            <StatusBadge
                                                status={opp.status}
                                                title={opp.status === INCOMPLETE_STATE && opp.missingFields?.length ? `Falta: ${missingFieldsText(opp.missingFields)}` : undefined}
                                            >
                                                {formatStatusLabel(opp.status)}
                                            </StatusBadge>
                                        </div>
                                    </OpportunityHeader>
                                    <OpportunityClient>📌 {opp.client}</OpportunityClient>
                                    {opp.status === INCOMPLETE_STATE && (opp.missingFields?.length ?? 0) > 0 && (
                                        <MissingNote>Falta: {missingFieldsText(opp.missingFields)}</MissingNote>
                                    )}
                                    <div style={{ marginBottom: '0.5rem' }}>
                                        <RedmineCardPicker
                                            opportunityId={parseInt(String(opp.id))}
                                            identifier={opp.redmineIdentifier}
                                            projectName={opp.redmineProjectName}
                                            canEdit={canLinkRedmine}
                                            onLinked={() => {
                                                loadOpportunities()
                                                // A new or removed link changes this card's forecast
                                                loadBillingStatuses()
                                            }}
                                        />
                                    </div>
                                    <Flex gap="0.5rem" wrap>
                                        <Badge>{formatStatusLabel(opp.stage)}</Badge>
                                        {opp.technologies?.slice(0, 3).map((tech) => (
                                            <Badge key={tech} style={{ background: '#e3f2fd', color: '#1976d2' }}>
                                                {tech}
                                            </Badge>
                                        ))}
                                    </Flex>
                                    <OpportunityMeta>
                                        {/* Show infra from assignments (source of truth) or proyecto_infra fallback */}
                                        {opp.infraAssignments && opp.infraAssignments.length > 0 ? (
                                            opp.infraAssignments.map((a, i) => (
                                                <MetaItem key={`infra-${i}`}>🖥️ {a.label}</MetaItem>
                                            ))
                                        ) : opp.proyectoInfra && (opp.proyectoInfra === 'local' || opp.proyectoInfra === 'cliente') ? (
                                            <MetaItem>🖥️ {infraMap[opp.proyectoInfra] || opp.proyectoInfra}</MetaItem>
                                        ) : null}
                                        {opp.value && opp.status?.toLowerCase() === 'ganada' && opp.tipoProyecto !== 'Proyecto interno' && (
                                            <MetaItem>💰 {formatCurrency(opp.value)}</MetaItem>
                                        )}
                                        {formatProbability(opp.probability) && (
                                            <MetaItem>📊 {formatProbability(opp.probability)}</MetaItem>
                                        )}
                                        {opp.team && opp.team.length > 0 ? (
                                            <MetaItem>👥 {opp.team.map(m => m.userName).join(', ')}</MetaItem>
                                        ) : opp.owner ? (
                                            <MetaItem>👤 {opp.owner}</MetaItem>
                                        ) : null}
                                        {opp.rolesRequeridos && opp.rolesRequeridos.length > 0 && (
                                            <Flex gap="0.25rem" wrap style={{ marginTop: '0.25rem' }}>
                                                {opp.rolesRequeridos.map((r, i) => {
                                                    const details: string[] = []
                                                    if (r.count && r.count > 1) details.push(`x${r.count}`)
                                                    if (r.horas_totales) details.push(`${r.horas_totales}h`)
                                                    else if ('cargabilidad' in r && r.cargabilidad) details.push(`${r.cargabilidad}%`)
                                                    const startAvail = (r as { start_availability?: string }).start_availability
                                                    if (startAvail && startAvail !== 'Inmediato') details.push(`desde ${startAvail}`)
                                                    const suffix = details.length ? ` (${details.join(' · ')})` : ''
                                                    return (
                                                        <Badge
                                                            key={`role-${i}`}
                                                            style={{ background: '#e8f5e9', color: '#2e7d32', fontSize: '0.72rem' }}
                                                        >
                                                            🎯 {r.role}{suffix}
                                                        </Badge>
                                                    )
                                                })}
                                            </Flex>
                                        )}
                                        <OpportunityBillingBadge status={billingStatuses[String(opp.id)]} />
                                    </OpportunityMeta>
                                </OpportunityCardStyled>
                            ))}
                        </OpportunitiesGrid>
                    )}
                </>
            )}

            <OpportunityModal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false)
                    setEditingOpportunity(null)
                    // The modal auto-saves straight through the API, so the list
                    // (which seeds the modal on the next open) and the billing
                    // badges are stale until reloaded. Reopening a card with the
                    // old object would send it back on the first edit and revert
                    // the edits auto-saved in the previous session.
                    loadOpportunities({ background: true })
                    loadBillingStatuses()
                }}
                onSave={handleSaveOpportunity}
                onDelete={handleDeleteOpportunity}
                opportunity={editingOpportunity}
                sectors={sectors}
                technologies={technologies}
                users={users}
                availableRoles={availableRoles}
                gestorSuggestions={autocompleteData.gestores}
                responsableSuggestions={autocompleteData.responsables}
                existingOpportunities={rawOpportunities.map((o: any) => ({
                    id: o.id,
                    name: o.general?.name || o.name || '',
                    oferta_code: o.oferta?.code || null,
                    proyecto_codigo: o.proyecto?.codigo || null,
                }))}
            />

            {/* SharePoint Preview Modal */}
            {previewData !== null && (() => {
                const eligible = previewData.filter((p) => p.status !== 'excluded' && typeof p.path === 'string')
                const eligiblePaths = eligible.map((p) => p.path as string)
                const selectedCount = eligiblePaths.filter((p) => selectedPaths.has(p)).length
                const matchesFilter = (p: any) => previewFilter === 'all' || p.status === previewFilter
                const visible = previewData.filter(matchesFilter)
                // "Seleccionar todas / Ninguna" act on the rows on screen, the rest keep their tick.
                const visiblePaths = visible
                    .filter((p) => p.status !== 'excluded' && typeof p.path === 'string')
                    .map((p) => p.path as string)
                const allSelected = visiblePaths.length > 0 && visiblePaths.every((p) => selectedPaths.has(p))
                const noneSelected = !visiblePaths.some((p) => selectedPaths.has(p))

                const selectAll = () => setSelectedPaths(new Set([...selectedPaths, ...visiblePaths]))
                const selectNone = () => setSelectedPaths(new Set([...selectedPaths].filter((p) => !visiblePaths.includes(p))))
                const filterOptions: { key: typeof previewFilter; label: string; count: number }[] = [
                    { key: 'all', label: 'Todas', count: previewData.length },
                    { key: 'ready', label: '✅ Correctas', count: previewData.filter((p) => p.status === 'ready').length },
                    { key: 'exists', label: '🔄 Existentes', count: previewData.filter((p) => p.status === 'exists').length },
                    { key: 'excluded', label: '⛔ Incorrectas', count: previewData.filter((p) => p.status === 'excluded').length },
                ]

                return (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', zIndex: 1000,
                }} onClick={() => setPreviewData(null)}>
                    <div style={{
                        background: 'white', borderRadius: '12px', width: '90%', maxWidth: '700px',
                        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                    }} onClick={(e) => e.stopPropagation()}>
                        <div style={{
                            padding: '1rem 1.5rem', borderBottom: '1px solid #eee',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.1rem' }}>📂 Preview importación SharePoint</h3>
                                <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>
                                    {previewData.filter(p => p.status === 'ready').length} nuevas,{' '}
                                    {previewData.filter(p => p.status === 'exists').length} existentes,{' '}
                                    {previewData.filter(p => p.status === 'excluded').length} excluidas
                                </div>
                            </div>
                            <button onClick={() => setPreviewData(null)} style={{
                                background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#999',
                            }}>&times;</button>
                        </div>
                        <div role="tablist" aria-label="Filtrar la previsualización" style={{
                            padding: '0.5rem 1.5rem', borderBottom: '1px solid #eee',
                            display: 'flex', gap: '0.4rem', flexWrap: 'wrap',
                        }}>
                            {filterOptions.map((opt) => (
                                <button
                                    key={opt.key}
                                    role="tab"
                                    aria-selected={previewFilter === opt.key}
                                    onClick={() => setPreviewFilter(opt.key)}
                                    disabled={opt.count === 0 && opt.key !== 'all'}
                                    style={{
                                        padding: '0.25rem 0.65rem', fontSize: '0.75rem', borderRadius: '999px',
                                        border: `1px solid ${previewFilter === opt.key ? '#1565C0' : '#ccc'}`,
                                        background: previewFilter === opt.key ? '#1565C0' : 'white',
                                        color: previewFilter === opt.key ? 'white' : opt.count === 0 ? '#bbb' : '#333',
                                        cursor: opt.count === 0 && opt.key !== 'all' ? 'default' : 'pointer',
                                    }}
                                >
                                    {opt.label} ({opt.count})
                                </button>
                            ))}
                        </div>
                        {eligible.length > 0 && (
                            <div style={{
                                padding: '0.5rem 1.5rem', borderBottom: '1px solid #eee',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                background: '#FAFAFA', fontSize: '0.8rem',
                            }}>
                                <span style={{ color: '#555' }}>
                                    Seleccionadas: <b>{selectedCount}</b> de {eligible.length}
                                </span>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button onClick={selectAll} disabled={allSelected} style={{
                                        padding: '0.25rem 0.6rem', fontSize: '0.75rem',
                                        border: '1px solid #1565C0', borderRadius: '4px',
                                        background: allSelected ? '#F5F5F5' : 'white',
                                        color: allSelected ? '#999' : '#1565C0',
                                        cursor: allSelected ? 'default' : 'pointer',
                                    }}>Seleccionar todas</button>
                                    <button onClick={selectNone} disabled={noneSelected} style={{
                                        padding: '0.25rem 0.6rem', fontSize: '0.75rem',
                                        border: '1px solid #999', borderRadius: '4px',
                                        background: noneSelected ? '#F5F5F5' : 'white',
                                        color: noneSelected ? '#999' : '#555',
                                        cursor: noneSelected ? 'default' : 'pointer',
                                    }}>Ninguna</button>
                                </div>
                            </div>
                        )}
                        <div style={{ overflowY: 'auto', padding: '0.75rem 1.5rem', flex: 1 }}>
                            {visible.length === 0 && (
                                <div style={{ color: '#777', fontSize: '0.85rem', padding: '0.5rem 0' }}>
                                    Ninguna carpeta en este filtro.
                                </div>
                            )}
                            {visible.map((item: any, i: number) => {
                                const isExcluded = item.status === 'excluded'
                                const isSelected = !isExcluded && selectedPaths.has(item.path)
                                return (
                                    <label key={i} style={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        padding: '0.5rem 0.75rem', marginBottom: '4px', borderRadius: '6px',
                                        background: item.status === 'ready' ? '#E8F5E9'
                                            : item.status === 'exists' ? '#E3F2FD'
                                            : '#FFF3E0',
                                        fontSize: '0.85rem',
                                        cursor: isExcluded ? 'not-allowed' : 'pointer',
                                        opacity: isExcluded ? 0.7 : (isSelected ? 1 : 0.65),
                                    }}>
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            disabled={isExcluded}
                                            onChange={() => toggleSelectedPath(item.path)}
                                            style={{ marginRight: '0.75rem', flexShrink: 0, cursor: isExcluded ? 'not-allowed' : 'pointer' }}
                                        />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.status === 'ready' ? '✅' : item.status === 'exists' ? '🔄' : '⛔'}{' '}
                                                {item.name}
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                                {item.sector}/{item.client}
                                                {item.code && item.code_kind && item.code_kind !== 'OT' && (
                                                    <span title={`Identificador ${item.code_kind}`}> · {item.code_kind} {item.code}</span>
                                                )}
                                            </div>
                                        </div>
                                        {item.reason && (
                                            <div style={{
                                                fontSize: '0.75rem', color: item.status === 'excluded' ? '#E65100' : '#1565C0',
                                                marginLeft: '0.5rem', textAlign: 'right', flexShrink: 0,
                                            }}>
                                                {item.reason}
                                            </div>
                                        )}
                                    </label>
                                )
                            })}
                        </div>
                        <div style={{
                            padding: '1rem 1.5rem', borderTop: '1px solid #eee',
                            display: 'flex', justifyContent: 'flex-end', gap: '0.5rem',
                        }}>
                            <button onClick={() => setPreviewData(null)} style={{
                                padding: '0.5rem 1rem', border: '1px solid #ccc', borderRadius: '6px',
                                background: 'white', cursor: 'pointer',
                            }}>Cancelar</button>
                            <button
                                onClick={handleConfirmSync}
                                disabled={selectedCount === 0}
                                style={{
                                    padding: '0.5rem 1.5rem', border: 'none', borderRadius: '6px',
                                    background: selectedCount === 0 ? '#9E9E9E' : '#1565C0',
                                    color: 'white',
                                    cursor: selectedCount === 0 ? 'not-allowed' : 'pointer',
                                    fontWeight: 600,
                                }}
                            >
                                Importar ({selectedCount} seleccionada{selectedCount === 1 ? '' : 's'})
                            </button>
                        </div>
                    </div>
                </div>
                )
            })()}
        </PageContainer>
    )
}
